import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

export type QueuedGrapheme = {
  grapheme: string;
  /** ISO instant the grapheme joined the queue. */
  queuedAt: string;
  userCardId: string;
};

// Queued and not yet enrolled: enrollment clears `queuedAt`, but a card enrolled on another device
// before that write syncs must not show up in the queue, so the FSRS link is checked too.
const QUEUED_GRAPHEMES_CONFIG = {
  columns: { id: true, queuedAt: true },
  where: { fsrsCardId: { isNull: true }, queuedAt: { isNotNull: true } },
  with: { userGraphemeMeaningCard: { columns: { grapheme: true } } },
} as const;

export function useGetQueuedGraphemesQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const withTableChangeVersion = useActorTableChangeVersion("userCard", QUEUED_GRAPHEMES_CONFIG);
  return () => withTableChangeVersion(["queuedGraphemes", { actorDb }] as const);
}

/** The learn queue, oldest first — the order its graphemes are enrolled in. */
export const [useQueuedGraphemesQueryOptions, queuedGraphemesQueryFn] = createUseQueryOptions(
  useGetQueuedGraphemesQueryKey,
  async ({ queryKey: [_, { actorDb }] }): Promise<QueuedGrapheme[]> => {
    // Soft-delete filter is injected by the wrapped `actorDb` query proxy.
    const queuedCards = await actorDb(import.meta.location).query.userCard.findMany(
      QUEUED_GRAPHEMES_CONFIG,
    );

    return queuedCards
      .flatMap((card): QueuedGrapheme[] => {
        const meaningCard = card.userGraphemeMeaningCard;
        if (meaningCard == null || card.queuedAt === null) {
          return [];
        }
        return [
          {
            grapheme: meaningCard.grapheme,
            queuedAt: card.queuedAt,
            userCardId: card.id,
          },
        ];
      })
      .sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  },
);
